// /payment-return — post-payment landing bridge for AzeriCard.
//
// Why this route exists:
//   AzeriCard's hosted-page integration sends the user's browser back to
//   the configured MERCH_URL after card entry (success OR cancel). There
//   is no per-transaction return URL in AzeriCard's API — MERCH_URL is a
//   single fixed value registered with the bank panel. Previously
//   MERCH_URL was https://yolutap.com (the site root), so the user
//   landed on the marketing homepage after paying. Hikmet (AzeriCard
//   integration contact) confirmed this is the expected behavior and
//   that a small bridge page is the recommended pattern.
//
// What this route does:
//   1. On mount, read `RETURN_TOKEN_STORAGE_KEY` from localStorage.
//      PayScreen writes the current session's access_token there right
//      before redirecting the browser to AzeriCard.
//   2. If a valid-shaped token is found, navigate to /session/<token>.
//      SessionLayout there picks the right next screen based on the
//      session status (paid → IntroScreen, pending_payment → PayScreen
//      again so the user can retry).
//   3. If no token (different device / private mode / cleared storage /
//      bookmarked URL), render a fallback that gives the user three
//      ways out: paste the token from the email receipt, start over at
//      /start, or contact support.
//
// What this route deliberately does NOT do:
//   - Touch payment status. That's the server-to-server callback's job
//     (/api/payment/azericard-callback). The bridge is pure navigation;
//     it never "confirms" anything.
//   - Read any AzeriCard fields from the URL/body. We don't trust
//     browser-side payment results — the signed S2S callback is the
//     only source of truth for paid/unpaid.
//   - Clear localStorage after redirect. PayScreen overwrites the key
//     on every new payment attempt, so stale values get refreshed
//     automatically. Clearing here would just lose the token if the
//     user happens to refresh /payment-return.

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { usePublishPageLanguage } from "@/lib/page-language";

import type { LanguageKey } from "@/lib/yolutap/types";
import { RETURN_TOKEN_STORAGE_KEY } from "@/lib/payment/return-storage";

// Same shape PayScreen accepts — 8-128 hex digits. If localStorage has
// something else (e.g., user manually tampered with storage), we treat
// it as missing and fall through to the recovery UI.
const ACCESS_TOKEN_RE = /^[0-9a-f]{8,128}$/i;

// ─── i18n ─────────────────────────────────────────────────────────────────────

// Fallback copy deliberately does NOT mention a receipt email — we
// haven't built that flow yet, so promising it would point parents
// at a dead end. Support contact is the primary recovery path; the
// manual token-paste option is preserved as a secondary path for the
// rare user who saved the session link from earlier in the flow.
//
// The receipt-email flow is on the post-launch fast-follow list
// (because without it, the fallback's secondary option is half-dead
// — we'd be telling parents to paste a token they were never given).

const T = {
  ru: {
    pageLanguageLabel: "Язык:",
    checking: "Возвращаем вас в сессию…",
    fallbackTitle: "Не удалось найти вашу сессию автоматически",
    fallbackIntro:
      "Если платёж прошёл, доступ уже открыт — мы получили подтверждение от банка отдельным каналом. " +
      "Самый быстрый способ вернуться в сессию — написать нам.",
    supportTitle: "Свяжитесь с поддержкой",
    supportBody:
      "Напишите на support@yolutap.com — укажите имя ребёнка и email родителя, мы найдём вашу сессию и пришлём ссылку.",
    tokenTitle: "Если у вас есть токен сессии",
    tokenBody:
      "Длинная hex-строка из ссылки на сессию (около 48 символов). Введите её, чтобы открыть свою сессию.",
    tokenInputLabel: "Токен сессии",
    tokenSubmit: "Открыть сессию",
    tokenInvalid: "Токен должен состоять из 8 и более hex-символов (0-9, a-f).",
    backHome: "На главную",
    startNew: "Создать новую сессию",
  },
  az: {
    pageLanguageLabel: "Dil:",
    checking: "Sizi sessiyaya qaytarırıq…",
    fallbackTitle: "Sessiyanızı avtomatik tapmaq mümkün olmadı",
    fallbackIntro:
      "Əgər ödəniş uğurla keçibsə, giriş artıq açıqdır — bank təsdiqini ayrı kanalla aldıq. " +
      "Sessiyaya qayıtmağın ən sürətli yolu — bizə yazmaqdır.",
    supportTitle: "Dəstəklə əlaqə",
    supportBody:
      "support@yolutap.com ünvanına yazın — uşağın adını və valideynin e-poçtunu göstərin, sessiyanı tapıb linki göndərəcəyik.",
    tokenTitle: "Əgər sessiya tokeni varsa",
    tokenBody:
      "Sessiya linkindəki uzun hex-sətir (təxminən 48 simvol). Sessiyanı açmaq üçün daxil edin.",
    tokenInputLabel: "Sessiya tokeni",
    tokenSubmit: "Sessiyanı aç",
    tokenInvalid: "Token 8 və daha çox hex-simvoldan ibarət olmalıdır (0-9, a-f).",
    backHome: "Ana səhifəyə",
    startNew: "Yeni sessiya yarat",
  },
  en: {
    pageLanguageLabel: "Language:",
    checking: "Returning you to your session…",
    fallbackTitle: "We couldn't find your session automatically",
    fallbackIntro:
      "If your payment went through, access is already open — we received the bank's confirmation on a separate channel. " +
      "The fastest way back into your session is to email us.",
    supportTitle: "Contact support",
    supportBody:
      "Email support@yolutap.com — include the child's name and parent's email, we'll find your session and send the link.",
    tokenTitle: "If you have your session token",
    tokenBody:
      "The long hex string from your session link (about 48 characters). Enter it to open your session.",
    tokenInputLabel: "Session token",
    tokenSubmit: "Open session",
    tokenInvalid:
      "Token must be 8 or more hex characters (0-9, a-f).",
    backHome: "Back to home",
    startNew: "Start a new session",
  },
} as const;

// ─── route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/payment-return")({
  head: () => ({
    meta: [
      { title: "Возврат с оплаты — Yolutap" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PaymentReturnPage,
});

function PaymentReturnPage() {
  const navigate = useNavigate();
  // SSR renders `phase: "checking"` so the page never flashes the
  // fallback UI on first paint. Phase flips to "fallback" only after
  // the client-side useEffect runs and confirms no token is available.
  const [phase, setPhase] = useState<"checking" | "fallback">("checking");
  const [language, setLanguage] = useState<LanguageKey>("ru");
  const [manualToken, setManualToken] = useState("");
  const [manualError, setManualError] = useState<string | null>(null);
  const t = T[language];
  // Переключатель языка меняет текст без перезагрузки — <html lang> за ним.
  usePublishPageLanguage(language);

  useEffect(() => {
    // localStorage is browser-only — guard the access for SSR + cases
    // where storage is unavailable (Safari private mode, quota errors,
    // sandboxed iframes). Any failure → fall through to recovery UI.
    if (typeof window === "undefined") return;
    let token: string | null = null;
    try {
      token = window.localStorage.getItem(RETURN_TOKEN_STORAGE_KEY);
    } catch {
      token = null;
    }
    if (token && ACCESS_TOKEN_RE.test(token)) {
      navigate({ to: "/session/$token", params: { token } });
      return;
    }
    setPhase("fallback");
  }, [navigate]);

  function onManualSubmit(e: React.FormEvent) {
    e.preventDefault();
    const v = manualToken.trim();
    if (!ACCESS_TOKEN_RE.test(v)) {
      setManualError(t.tokenInvalid);
      return;
    }
    setManualError(null);
    navigate({ to: "/session/$token", params: { token: v } });
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {/* Language toggle stays minimal — three short buttons. The
            fallback content is the only thing the user actually sees;
            the "checking" phase is usually a <500ms flash. */}
        <div style={langRowStyle}>
          <span style={pageLangLabelStyle}>{t.pageLanguageLabel}</span>
          {(["ru", "az", "en"] as const).map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLanguage(l)}
              style={{
                ...langBtnStyle,
                background: l === language ? "#1E5A6B" : "transparent",
                color: l === language ? "#fff" : "#6B8090",
                borderColor: l === language ? "#1E5A6B" : "#E3E7EB",
              }}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>

        {phase === "checking" ? (
          <div style={checkingStyle}>
            <p style={checkingTextStyle}>{t.checking}</p>
          </div>
        ) : (
          <>
            <h1 style={titleStyle}>{t.fallbackTitle}</h1>
            <p style={introStyle}>{t.fallbackIntro}</p>

            {/* Primary: contact support. This is the realistic recovery
                path until a receipt-email flow exists that gives the
                parent the session token. Lead with what works. */}
            <section style={optionStyle}>
              <h2 style={optionTitleStyle}>{t.supportTitle}</h2>
              <p style={optionBodyStyle}>{t.supportBody}</p>
              <a href="mailto:support@yolutap.com" style={mailLinkStyle}>
                support@yolutap.com
              </a>
            </section>

            {/* Secondary: paste a token the parent might have from the
                session URL (e.g. they bookmarked /session/<token>
                earlier). No "from your email" promise — that flow is
                not yet built. */}
            <section style={optionStyle}>
              <h2 style={optionTitleStyle}>{t.tokenTitle}</h2>
              <p style={optionBodyStyle}>{t.tokenBody}</p>
              <form onSubmit={onManualSubmit} style={{ marginTop: 10 }}>
                <label style={labelStyle}>
                  {t.tokenInputLabel}
                  <input
                    type="text"
                    value={manualToken}
                    onChange={(e) => setManualToken(e.target.value)}
                    minLength={8}
                    maxLength={128}
                    autoComplete="off"
                    style={inputStyle}
                  />
                </label>
                {manualError && (
                  <p style={{ color: "#8B2D00", fontSize: 13, marginTop: 8 }}>
                    {manualError}
                  </p>
                )}
                <button type="submit" style={submitStyle}>
                  {t.tokenSubmit}
                </button>
              </form>
            </section>

            <div style={footerRowStyle}>
              <a href="/" style={footerLinkStyle}>
                ← {t.backHome}
              </a>
              <a href="/start" style={footerLinkStyle}>
                {t.startNew} →
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── styles ───────────────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  background: "#F0EDE5",
  fontFamily: "'Nunito Sans', system-ui, sans-serif",
  padding: "40px 16px",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  color: "#0F2A38",
};
const cardStyle: React.CSSProperties = {
  background: "#FBFAF7",
  border: "1px solid #E3E7EB",
  borderRadius: 20,
  padding: 32,
  maxWidth: 560,
  width: "100%",
};
const langRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  justifyContent: "flex-end",
  marginBottom: 16,
};
const pageLangLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "#6B8090",
  marginRight: 4,
};
const langBtnStyle: React.CSSProperties = {
  border: "1px solid #E3E7EB",
  borderRadius: 8,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "inherit",
};
const checkingStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 20,
  padding: "60px 0",
};
const checkingTextStyle: React.CSSProperties = {
  fontSize: 15,
  color: "#6B8090",
  fontWeight: 600,
  margin: 0,
};
const titleStyle: React.CSSProperties = {
  fontFamily: "'Source Serif 4', Georgia, serif",
  fontSize: 24,
  fontWeight: 700,
  color: "#143F4B",
  marginBottom: 12,
};
const introStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#6B8090",
  lineHeight: 1.6,
  marginBottom: 24,
};
const optionStyle: React.CSSProperties = {
  border: "1px solid #E3E7EB",
  borderRadius: 12,
  padding: "16px 18px",
  marginBottom: 12,
  background: "#fff",
};
const optionTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: "#143F4B",
  margin: 0,
  marginBottom: 6,
};
const optionBodyStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#0F2A38",
  lineHeight: 1.5,
  margin: 0,
};
const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#6B8090",
  marginTop: 12,
};
const inputStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  marginTop: 6,
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #E3E7EB",
  fontSize: 14,
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  background: "#fff",
  color: "#0F2A38",
  boxSizing: "border-box",
};
const submitStyle: React.CSSProperties = {
  marginTop: 12,
  background: "#1E5A6B",
  color: "#fff",
  border: "none",
  borderRadius: 10,
  padding: "10px 18px",
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
};
const mailLinkStyle: React.CSSProperties = {
  display: "inline-block",
  marginTop: 8,
  fontSize: 14,
  fontWeight: 700,
  color: "#1E5A6B",
  textDecoration: "underline",
};
const footerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: 24,
  fontSize: 13,
  flexWrap: "wrap",
  gap: 12,
};
const footerLinkStyle: React.CSSProperties = {
  color: "#1E5A6B",
  textDecoration: "none",
  fontWeight: 600,
};
